const m = require('minthril');
const { component } = require('../../component');
const { DomainProvisioning } = require('../../components/domain-provisioning');
const { Field } = require('../../components/field');
const { SiteNavigation } = require('../../components/site-navigation');
const { RendererSelect } = require('../../components/renderer-select');

const SettingsPage = component((vnode, emit) => {
  let billingAccessAccountId = null;
  let billingAccessStatus = 'unloaded';
  let canConnectCustomDomain = false;
  let domainTrialEndsAt = null;

  return {
    view: ({ attrs: {
      form,
      site,
      accountId,
      loadBillingAccess,
      saving,
      removing,
      provisioningDomain,
      section = 'site',
      onSave,
      onChange,
      onAddCapture,
      onAddIgnoredRoute,
      onRemoveIgnoredRoute,
      onAddDomain,
      onRefreshDomain,
      onTestDomain,
      isDomainProvisioning,
      isDomainRemoving,
      onRemoveDomain,
      onRemove,
      onClearCache,
      cacheWebhookUrl,
      onCreateCacheWebhook,
      onRevokeCacheWebhook
    } }) => {
      const backPath = '/sites';
      const backText = '← All sites';
      const heading = form.id ? 'Site settings' : 'Add a site';
      const settingsPath = `/sites/${form.id}/settings`;
      const sections = [
        ['site', 'Site'],
        ['domains', 'Domains'],
        ['rendering', 'Rendering'],
        ['cache', 'Cache'],
        ['captures', 'Capture rules'],
        ['webhooks', 'Webhooks'],
        ['maintenance', 'Maintenance']
      ];
      const activeSection = sections.some(([value]) => value === section) ? section : 'site';

      if (form.id && activeSection === 'domains' && accountId &&
        billingAccessAccountId !== accountId) {
        billingAccessAccountId = accountId;
        billingAccessStatus = 'loading';
        canConnectCustomDomain = false;
        domainTrialEndsAt = null;
        Promise.resolve().then(loadBillingAccess).then(access => {
          if (billingAccessAccountId !== accountId) return;
          canConnectCustomDomain = access.canConnectCustomDomain === true;
          domainTrialEndsAt = access.domainTrialEndsAt || null;
          billingAccessStatus = 'loaded';
          emit('redraw');
        }).catch(error => {
          if (billingAccessAccountId !== accountId) return;
          console.error('Unable to check custom-domain billing access:', error);
          billingAccessStatus = 'failed';
          emit('redraw');
        });
      }

      const saveButton = () => m('button.button', {
        disabled: saving || removing
      }, saving
        ? form.id ? 'Updating site…' : 'Creating site…'
        : form.id ? 'Update site' : 'Save site');

      return m('section.panel',
        form.id && m(SiteNavigation, {
          breadcrumbs: [
            { label: backPath.startsWith('/admin') ? 'Admin' : 'Sites', href: backPath },
            { label: site.name, href: `/sites/${form.id}` },
            { label: 'Settings' }
          ],
          overviewHref: `/sites/${form.id}`,
          trafficHref: `/sites/${form.id}/traffic`,
          routesHref: `/sites/${form.id}/routes`,
          costHref: `/sites/${form.id}/cost`,
          settingsHref: `/sites/${form.id}/settings`,
          current: 'settings'
        }),
        !form.id && m('a.link-button', { href: backPath }, backText),
        m('h2', heading),
        form.id && m('nav.settings-tabs', {
          'aria-label': 'Site settings sections'
        }, sections.map(([value, label]) => m('a', {
          class: activeSection === value ? 'settings-tabs__tab--current' : '',
          href: value === 'site' ? settingsPath : `${settingsPath}/${value}`
        }, label))),
        m('form', { onsubmit: onSave },
          saving && m('p.settings-saving', [
            m('span.settings-saving__spinner', { 'aria-hidden': 'true' }),
            form.id ? 'Updating your site…' : 'Creating your site…'
          ]),
          activeSection === 'site' && m('section.settings-section',
            m('h3', 'Site'),
            m(Field, { label: 'Name', value: form.name, onchange: onChange('name') }),
            m(Field, {
              label: 'Where your SPA is hosted',
              help: 'The URL Spache fetches and renders behind your public domain.',
              placeholder: 'https://your-app.example.com',
              value: form.origin,
              onchange: onChange('origin')
            }),
            saveButton()
          ),
          form.id && activeSection === 'domains' && m('section.settings-section',
            m('h3', 'Preview domain'),
            m(Field, {
              label: 'Preview subdomain',
              help: 'The lowercase subdomain used for your private Spache preview URL.',
              placeholder: 'my-site',
              value: form.previewSlug,
              onchange: onChange('previewSlug')
            }),
            saveButton()
          ),
          form.id && activeSection === 'domains' && m('section.settings-section',
            m('h3', 'Connected domains'),
            site && m(DomainProvisioning, {
              site,
              onrefresh: onRefreshDomain,
              ontest: onTestDomain,
              isProvisioning: isDomainProvisioning,
              isRemoving: isDomainRemoving,
              onRemove: onRemoveDomain
            })
          ),
          form.id && activeSection === 'domains' && m('section.settings-section', [
            m('h3', canConnectCustomDomain ? 'Add public domain' : 'Custom domains'),
            canConnectCustomDomain
              ? [
                m(Field, {
                  label: 'Domain',
                  value: form.domain,
                  placeholder: 'www.example.com',
                  onchange: onChange('domain')
                }),
                m('p.muted', 'Add each hostname people can visit, such as www.example.com. Use no protocol or path.'),
                m('button.link-button', {
                  type: 'button',
                  disabled: provisioningDomain,
                  onclick: onAddDomain
                }, provisioningDomain ? 'Adding domain…' : 'Add domain'),
                domainTrialEndsAt && m('p.muted', `Pilot trial: one custom hostname, no charge. Trial ends ${new Date(domainTrialEndsAt).toLocaleString()}. After it ends, requests pass through to your origin without Spache cache or rendering.`)
              ]
              : m('div.settings-domain-upgrade',
                billingAccessStatus === 'loading' || billingAccessStatus === 'unloaded'
                  ? 'Checking account eligibility…'
                  : billingAccessStatus === 'failed'
                    ? 'Unable to check billing eligibility. Refresh this page and try again.'
                    : 'Custom domains are a paid feature and will be available when paid plans launch. For now, use your Spache preview domain to view your site.'
              )
          ]),
          form.id && activeSection === 'rendering' && m('section.settings-section',
            m('h3', 'Rendering'),
            m(RendererSelect, {
              value: form.renderer,
              disabled: saving,
              onchange: onChange('renderer')
            }),
            m('p.muted', 'Routes use this renderer unless they have their own override in Route settings.'),
            m('div.form-row',
              m(Field, {
                label: 'Minimum wait (ms)',
                help: 'Always wait at least this long after the page loads before saving the render. Use this for predictable startup delays.',
                value: form.minimum,
                onchange: onChange('minimum')
              }),
              m(Field, {
                label: 'Quiet wait (ms)',
                help: 'After the minimum wait, keep waiting until the page has made no requests for this long. Increase it for APIs that arrive in bursts.',
                value: form.quiet,
                onchange: onChange('quiet')
              })
            ),
            saveButton()
          ),
          form.id && activeSection === 'cache' && m('section.settings-section',
            m('h3', 'Cache'),
            m(Field, {
              label: 'Cache lifetime (seconds)',
              type: 'number',
              min: 60,
              step: 60,
              value: form.cacheTtl,
              onchange: onChange('cacheTtl')
            }),
            m('p.muted', 'The cached page is kept for at least this long before Spache checks for changes. Minimum 60 seconds.'),
            saveButton()
          ),
          form.id && activeSection === 'cache' && m('section.settings-section',
            m('h3', 'Ignored routes'),
            m('p.muted',
              'Matching paths bypass Spache’s cache and renderer. Requests and responses are forwarded directly to your origin.'
            ),
            m('p.muted', 'Use * to match any part of the path. /api* matches /api and /api/anything; /api/* matches paths below /api/. Query strings are ignored.'),
            m('div.ignored-route-list', form.ignoredRoutes.map((route, index) => m('div.ignored-route-row', [
              m(Field, {
                label: `Path pattern ${index + 1}`,
                value: route,
                placeholder: '/api/*',
                onchange: onChange('ignoredRoute', index)
              }),
              m('button.button.button--quiet', {
                type: 'button',
                'aria-label': `Remove path pattern ${index + 1}`,
                onclick: () => onRemoveIgnoredRoute(index)
              }, 'Remove')
            ]))),
            m('button.button.button--quiet', { type: 'button', onclick: onAddIgnoredRoute }, 'Add ignored route'),
            saveButton()
          ),
          form.id && activeSection === 'cache' && m('section.settings-clear-cache', [
            m('h3', 'Clear cache'),
            m('p', 'Remove cached pages so the next request fetches a fresh version from your origin.'),
            m('button.settings-clear-cache__button', {
              type: 'button',
              onclick: onClearCache
            }, 'Clear cache')
          ]),
          form.id && activeSection === 'captures' && m('section.settings-section',
            m('h3', 'Capture rules'),
            m('p.muted',
              'GET requests are cached by default.'
            ),
            m('p.muted',
              'Add a rule only when page content comes from a non-GET request, such as a POST GraphQL request.'
            ),
            m('p.muted',
              'It tells Spache which response to save and check when deciding whether to update the cached page.'
            ),
            m('p.muted',
              'Wildcard rules use * to match any part of the URL. For Shopify API versions, use https://your-store.myshopify.com/api/*/graphql.json.'
            ),
            form.captures.map((capture, index) => m('section.panel.capture-row',
              m(Field, {
                label: `Request ${index + 1}`,
                value: capture.url,
                placeholder: 'https://your-store.myshopify.com/api/*/graphql.json',
                onchange: onChange('captureUrl', index)
              }),
              m('select', {
                value: capture.match,
                onchange: onChange('captureMatch', index)
              },
              m('option', { value: 'exact' }, 'Exact URL'),
              m('option', { value: 'wildcard' }, 'Wildcard URL'),
              m('option', { value: 'regex' }, 'Regular expression')),
              m('select', {
                value: capture.method,
                onchange: onChange('captureMethod', index)
              },
              m('option', { value: 'GET' }, 'GET'),
              m('option', { value: 'POST' }, 'POST'))
            )),
            m('button.link-button', { type: 'button', onclick: onAddCapture }, 'Add capture rule'),
            saveButton()
          ),
          form.id && activeSection === 'webhooks' && m('section.settings-section', [
            m('h3', 'Cache invalidation webhook'),
            m('p.muted', 'Use this webhook after a successful deployment or content update to invalidate cached pages. The URL is shown only after it is generated; save it in your deployment provider’s secret store.'),
            cacheWebhookUrl && m('p.settings-webhook-url', cacheWebhookUrl),
            cacheWebhookUrl
              ? m('button.link-button', {
                type: 'button',
                onclick: onRevokeCacheWebhook
              }, 'Revoke webhook')
              : m('button.link-button', {
                type: 'button',
                onclick: onCreateCacheWebhook
              }, 'Generate webhook URL'),
            m('a.link-button', { href: '/docs/cache-invalidation' }, 'Read cache invalidation docs')
          ]),
          form.id && activeSection === 'maintenance' && m('section.settings-remove', [
            m('h3', 'Remove site'),
            m('p', 'This stops Spache proxying this site. Cached data and historical usage records are retained.'),
            m('button.settings-remove__button', {
              type: 'button',
              disabled: removing,
              onclick: onRemove
            }, removing ? 'Removing site…' : 'Remove site')
          ])
        )
      );
    }
  };
});

module.exports = { SettingsPage };
